import { useEffect, useRef, useState } from "react";
import { Car, Loader2 } from "lucide-react";
import { CodiceFiscale } from "codice-fiscale-js";
import api from "../lib/api";
import AutocompleteInput from "./AutocompleteInput";
import MultiCellulare from "./MultiCellulare";
import DateInput from "./DateInput";
import AddressAutocomplete from "./AddressAutocomplete";
import DecodificaCFButton from "./DecodificaCFButton";
import { ValidationHint } from "./ValidationHint";
import { validateCodiceFiscale } from "../lib/validators";

const LIGHT_INPUT = "h-9 rounded-md bg-white border border-slate-400 px-2.5 text-sm text-black focus:outline-none focus:ring-1 focus:ring-teal-500/50 w-full";
const LBL = "text-black text-xs uppercase tracking-wider font-mono";

// true solo per i ruoli che richiedono la domanda sul conducente:
// "Proprietario", "Cedente", "Assicurato". Nascosta per tutti gli altri ruoli.
export function conducenteRoleTriggers(role) {
  const s = String(role || "").trim().toLowerCase();
  return ["proprietario", "cedente", "assicurato"].includes(s);
}

const condName = (c) => (c.nominativo || [c.cognome, c.nome].filter(Boolean).join(" ")).trim();

// Ricerca predittiva sui conducenti già inseriti in altre pratiche (campo Cognome / Nome).
function CondNameSearch({ label, field, value, onType, onPick, testId, wrapClass = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);
  const focused = useRef(false);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const run = (q) => {
    if (timer.current) clearTimeout(timer.current);
    if (!q || q.trim().length < 2) { setItems([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/studio/conducenti/search", { params: { q: q.trim() } });
        const arr = Array.isArray(data) ? data : [];
        setItems(arr);
        if (focused.current) setOpen(arr.length > 0);
      } catch { setItems([]); }
      finally { setLoading(false); }
    }, 250);
  };

  return (
    <label className={`flex flex-col gap-1.5 relative ${wrapClass}`} ref={boxRef}>
      <span className={LBL}>{label}</span>
      <input value={value ?? ""} autoComplete="off"
        onChange={(e) => { const v = e.target.value.toUpperCase(); onType(v); run(v); }}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; }}
        data-testid={`${testId}-${field}`} className={LIGHT_INPUT} />
      {loading && <Loader2 className="absolute right-2.5 top-8 h-4 w-4 animate-spin text-slate-500" />}
      {open && items.length > 0 && (
        <div className="absolute z-50 top-full mt-1 w-full max-h-56 overflow-y-auto rounded-md border border-slate-300 bg-white shadow-xl" data-testid={`${testId}-${field}-list`}>
          {items.map((it, i) => (
            <button type="button" key={i} onMouseDown={(e) => { e.preventDefault(); onPick(it); setOpen(false); }}
              data-testid={`${testId}-${field}-opt`}
              className="w-full text-left px-3 py-2.5 text-sm text-black hover:bg-slate-100 border-b border-slate-200 last:border-0">
              <span className="font-medium">{condName(it)}</span>
              {it.codice_fiscale ? <span className="ml-2 text-xs font-mono text-slate-500">{it.codice_fiscale}</span> : null}
            </button>
          ))}
        </div>
      )}
    </label>
  );
}

export default function ConducenteFormSection({ value, onChange, role, subjectWord = "il soggetto", testId = "cond" }) {
  const [cfBlur, setCfBlur] = useState(false);
  const [cfManual, setCfManual] = useState(false);
  const d = value && typeof value === "object" ? value : {};
  const triggers = conducenteRoleTriggers(role);

  // Calcolo del Codice Fiscale lato client (nessuna chiamata esterna) dai dati anagrafici.
  const computeCF = () => {
    try {
      const parts = String(d.data_nascita || "").split("-");
      if (parts.length !== 3) return null;
      const [y, m, g] = parts;
      return new CodiceFiscale({
        name: d.nome, surname: d.cognome,
        gender: /^f/i.test(String(d.sesso || "")) ? "F" : "M",
        day: +g, month: +m, year: +y,
        birthplace: d.comune_nascita, birthplaceProvincia: d.comune_nascita_prov || undefined,
      }).toString();
    } catch { return null; }
  };

  // Ricalcolo automatico del CF quando cognome/nome/data/sesso/comune sono compilati,
  // finché l'utente non modifica il CF a mano (omocodia/correzioni).
  useEffect(() => {
    if (!triggers || cfManual) return;
    if (!d.nome || !d.cognome || !d.sesso || !d.comune_nascita || !d.data_nascita) return;
    const cf = computeCF();
    if (cf && cf !== d.codice_fiscale) onChange({ ...d, is_cliente: false, codice_fiscale: cf });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, cfManual, d.nome, d.cognome, d.sesso, d.comune_nascita, d.comune_nascita_prov, d.data_nascita]);

  if (!triggers) return null;

  const isYes = d.is_cliente === false;
  const setYes = () => onChange({ ...d, is_cliente: false });
  const setNo = () => onChange({ is_cliente: true });
  const ANAG = ["cognome", "nome", "sesso", "data_nascita", "comune_nascita", "comune_nascita_prov"];
  const upd = (k, v) => { if (ANAG.includes(k)) setCfManual(false); onChange({ ...d, is_cliente: false, [k]: v }); };
  const merge = (patch) => onChange({ ...d, is_cliente: false, ...patch });
  const pickConducente = (it) => { setCfManual(true); onChange({ ...it, is_cliente: false }); };

  const plain = (label, k, type, span = "md:col-span-2") => (
    <label className={`flex flex-col gap-1 min-w-0 ${span}`}>
      <span className={LBL}>{label}</span>
      {type === "date"
        ? <DateInput value={d[k] || ""} onChange={(v) => upd(k, v)} testId={`${testId}-${k}`} className={LIGHT_INPUT} />
        : <input type={type || "text"} value={d[k] ?? ""} onChange={(e) => upd(k, e.target.value)} data-testid={`${testId}-${k}`} className={LIGHT_INPUT} />}
    </label>
  );

  return (
    <div className="mt-5 rounded-lg border border-slate-300 bg-[#f0f0f0] p-4 text-black" data-testid={`${testId}-section`}>
      <div className="flex flex-wrap items-center gap-4">
        <h3 className="font-head text-base uppercase tracking-wide text-black flex items-center gap-2"><Car className="h-5 w-5" /> Conducente</h3>
        <span className="text-base text-black font-medium">C'è un conducente per questa pratica?</span>
        <div className="flex items-center gap-2" data-testid={`${testId}-sino`}>
          <button type="button" onClick={setYes} data-testid={`${testId}-si`} className={`px-4 py-1.5 rounded text-base border transition-colors ${isYes ? "bg-teal-600 border-teal-500 text-white" : "border-slate-400 text-slate-800 hover:bg-slate-200"}`}>Sì</button>
          <button type="button" onClick={setNo} data-testid={`${testId}-no`} className={`px-4 py-1.5 rounded text-base border transition-colors ${!isYes ? "bg-slate-600 border-slate-600 text-white" : "border-slate-400 text-slate-800 hover:bg-slate-200"}`}>No</button>
        </div>
      </div>
      {isYes ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-12 gap-x-3 gap-y-3" data-testid={`${testId}-fields`}>
          <CondNameSearch label="Cognome" field="cognome" value={d.cognome} onType={(v) => upd("cognome", v)} onPick={pickConducente} testId={testId} wrapClass="md:col-span-4" />
          <CondNameSearch label="Nome" field="nome" value={d.nome} onType={(v) => upd("nome", v)} onPick={pickConducente} testId={testId} wrapClass="md:col-span-3" />
          <label className="flex flex-col gap-1 min-w-0 md:col-span-2">
            <span className={LBL}>Sesso</span>
            <select value={/^f/i.test(String(d.sesso || "")) ? "Femmina" : /^m/i.test(String(d.sesso || "")) ? "Maschio" : ""}
              onChange={(e) => upd("sesso", e.target.value)} data-testid={`${testId}-sesso`} className={LIGHT_INPUT}>
              <option value="">Seleziona</option>
              <option value="Maschio">Maschio</option>
              <option value="Femmina">Femmina</option>
            </select>
          </label>
          {plain("Data nascita", "data_nascita", "date", "md:col-span-3")}
          <label className="flex flex-col gap-1 min-w-0 md:col-span-6">
            <span className={LBL}>Comune nascita</span>
            <AutocompleteInput value={d.comune_nascita} onChange={(v) => { if (String(v || "").toLowerCase() !== String(d.comune_nascita || "").toLowerCase()) { setCfManual(false); merge({ comune_nascita: v, comune_nascita_prov: "" }); } else if (v !== d.comune_nascita) upd("comune_nascita", v); }}
              onSelectItem={(it) => { setCfManual(false); merge({ comune_nascita: it.nome, comune_nascita_prov: it.provincia || "" }); }}
              source="comuni" testId={`${testId}-comune_nascita`} className="h-9 bg-white border-slate-400 text-black text-sm" />
          </label>
          <label className="flex flex-col gap-1 min-w-0 md:col-span-6">
            <span className={LBL}>Codice Fiscale</span>
            <div className="flex items-center gap-2">
              <input value={d.codice_fiscale ?? ""} onChange={(e) => { setCfManual(true); upd("codice_fiscale", e.target.value.toUpperCase()); }} onBlur={() => setCfBlur(true)} data-testid={`${testId}-codice_fiscale`} className={`${LIGHT_INPUT} flex-1 font-mono`} />
              <DecodificaCFButton cf={d.codice_fiscale} testId={`${testId}-decodifica-cf`} className="h-9 shrink-0"
                onDecode={(dec) => { setCfManual(true); merge({
                  data_nascita: dec.data_nascita_display || d.data_nascita,
                  sesso: dec.sesso === "F" ? "Femmina" : "Maschio",
                  comune_nascita: dec.comune_nascita || d.comune_nascita,
                  cognome: d.cognome || dec.cognomeCode,
                  nome: d.nome || dec.nomeCode,
                }); }} />
            </div>
            <ValidationHint result={validateCodiceFiscale(d.codice_fiscale)} show={cfBlur} testId={`${testId}-cf`} />
          </label>

          {plain("Patente n.", "patente_n", "text", "md:col-span-4")}
          {plain("Cat. patente", "patente_categoria", "text", "md:col-span-2")}
          {plain("Rilascio patente", "patente_rilascio", "date", "md:col-span-3")}
          {plain("Scadenza patente", "patente_scadenza", "date", "md:col-span-3")}

          <label className="flex flex-col gap-1 min-w-0 md:col-span-9">
            <span className={LBL}>Indirizzo</span>
            <AddressAutocomplete value={d.indirizzo} onChange={(v) => upd("indirizzo", v)}
              onPick={(pk) => merge({ indirizzo: pk.indirizzo, comune: pk.comune, cap: pk.cap, provincia: pk.provincia })}
              city={d.comune} prov={d.provincia} testId={`${testId}-indirizzo`} inputClassName="h-9 bg-white border-slate-400 text-black text-sm" />
          </label>
          {plain("N. civico", "numero_civico", "text", "md:col-span-3")}

          {plain("Comune", "comune", "text", "md:col-span-6")}
          {plain("CAP", "cap", "text", "md:col-span-3")}
          {plain("Provincia", "provincia", "text", "md:col-span-3")}

          {plain("Email", "email", "text", "md:col-span-6")}
          <label className="flex flex-col gap-1 min-w-0 md:col-span-6">
            <span className={LBL}>Cellulare</span>
            <MultiCellulare extra={d.cellulari_extra} onExtra={(v) => upd("cellulari_extra", v)} labels={d.cellulari_etichette} onLabels={(v) => upd("cellulari_etichette", v)} testId={`${testId}-cellulare`} inputClassName={LIGHT_INPUT}>
              <input type="tel" value={d.cellulare ?? ""} onChange={(e) => upd("cellulare", e.target.value)} data-testid={`${testId}-cellulare`} className={`${LIGHT_INPUT} w-full`} />
            </MultiCellulare>
          </label>
        </div>
      ) : (
        <p className="mt-3 text-sm text-slate-700">Alla guida c'era {subjectWord}. Nessun conducente separato.</p>
      )}
    </div>
  );
}
